/**
 * Client for the scoped audit history (`GET /api/v1/admin/audit-events/`,
 * RBAC Phase 6, DEV-5493 / DEV-5969).
 *
 * The endpoint is read-only and tenant-scoped server-side: cloud-core returns
 * only the rows inside the caller's companies (plus company-less rows that
 * target a user the caller administers), newest first, page-number paginated.
 * While `ADMIN_CONSOLE_ENABLED` is off it answers 404 like the rest of the
 * admin surface, and a non-admin caller gets 403.
 *
 * The row is camelCased by cloud-core's renderer **except** `before`/`after`:
 * those snapshots keep the keys the write recorded (`is_active`, not
 * `isActive`), so the UI renders them as stored and never renames them.
 *
 * Query parameters are `snake_case` like the rest of the API's query strings.
 * An unusable filter answers 400 rather than being ignored; the caller shows
 * that error instead of a page of unrelated history.
 */
import API from "Utils/api";

/** `AuditEvent.Action` values, verbatim from cloud-core. */
export const AUDIT_ACTIONS = [
  "user.created",
  "user.deactivated",
  "user.reactivated",
  "role.assigned",
  "role.revoked",
  "company.assigned",
  "company.revoked",
  "login.success",
  "login.failure",
  "permission.denied",
  "entry-permission.created",
  "entry-permission.released",
  "entry-permission.revoked",
  "vehicle.token.issued",
  "vehicle.token.refused",
  "vehicle.token.reacquired",
  "audit.redacted",
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export type AuditSnapshot = Record<string, unknown> | null;

export interface AuditEvent {
  id: string;
  occurredAt: string;
  action: string;
  actorUserId: string | null;
  actorEmail: string;
  actorServiceId: string;
  targetType: string;
  targetId: string;
  companyId: string | null;
  before: AuditSnapshot;
  after: AuditSnapshot;
  requestId: string;
}

export interface AuditEventPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: AuditEvent[];
}

/** The filters `AuditEventQuerySerializer` accepts. */
export interface AuditEventFilters {
  targetUser?: string;
  action?: string;
  occurredAfter?: string;
  occurredBefore?: string;
}

/** `AuditEventPagination`: default 50, `page_size` capped at 200. */
export const AUDIT_DEFAULT_PAGE_SIZE = 50;
export const AUDIT_MAX_PAGE_SIZE = 200;

export interface AuditEventQuery extends AuditEventFilters {
  page?: number;
  pageSize?: number;
}

/**
 * Builds the `snake_case` query string. Blank filters are dropped so an empty
 * form field is "no filter", not an invalid one.
 */
export const buildAuditEventParams = (
  query: AuditEventQuery
): Record<string, string> => {
  const params: Record<string, string> = {};
  const set = (key: string, value: string | number | undefined) => {
    if (value === undefined || value === null) return;
    const text = String(value).trim();
    if (text) params[key] = text;
  };
  set("target_user", query.targetUser);
  set("action", query.action);
  set("occurred_after", query.occurredAfter);
  set("occurred_before", query.occurredBefore);
  set("page", query.page);
  set(
    "page_size",
    query.pageSize === undefined
      ? undefined
      : Math.min(Math.max(1, query.pageSize), AUDIT_MAX_PAGE_SIZE)
  );
  return params;
};

export const getAdminAuditEvents = async (
  query: AuditEventQuery
): Promise<AuditEventPage> => {
  const response = await API.get("admin/audit-events/", {
    params: buildAuditEventParams(query),
  });
  return response.data;
};
